import { Box, Button, Chip, Typography } from "@mui/material";
import React from "react";

import { optionLabel, optionValue } from "./columnValues";
import { findOperator, isActiveFilterItem } from "./filterOperators";
import type { DataTableColDef, FilterItem, FilterModel } from "./types";

function valueLabel<R>(column: DataTableColDef<R>, value: unknown): string {
  const label = (raw: unknown) => {
    const match = column.valueOptions?.find(
      (option) => optionValue(option) === raw,
    );
    return match ? optionLabel(match) : String(raw);
  };
  return Array.isArray(value) ? value.map(label).join(", ") : label(value);
}

function chipLabel<R>(column: DataTableColDef<R>, item: FilterItem): string {
  const operator = findOperator(column, item.operator);
  const name = column.headerName ?? column.field;
  if (!operator) return name;
  if (operator.input === "none") return `${name} ${operator.label}`;
  return `${name} ${operator.label} ${valueLabel(column, item.value)}`;
}

interface FilterChipsProps<R> {
  columns: DataTableColDef<R>[];
  filterModel: FilterModel;
  onFilterModelChange: (model: FilterModel) => void;
  onEdit: (field: string) => void;
}

export default function FilterChips<R>({
  columns,
  filterModel,
  onFilterModelChange,
  onEdit,
}: FilterChipsProps<R>) {
  const byField = new Map(columns.map((column) => [column.field, column]));
  const active = filterModel.items.filter((item) => {
    const column = byField.get(item.field);
    return column && isActiveFilterItem(item, column);
  });
  if (!active.length) return null;
  const joiner = (filterModel.logicOperator ?? "and").toUpperCase();

  return (
    <Box
      className="DataTable-filterChips"
      aria-label="Active filters"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1,
        padding: "4px 4px 12px",
      }}
    >
      {active.map((item, index) => {
        const column = byField.get(item.field);
        if (!column) return null;
        return (
          <React.Fragment key={item.id}>
            {index > 0 && (
              <Typography variant="caption" color="text.secondary">
                {joiner}
              </Typography>
            )}
            <Chip
              size="small"
              label={chipLabel(column, item)}
              onClick={() => onEdit(item.field)}
              onDelete={() =>
                onFilterModelChange({
                  ...filterModel,
                  items: filterModel.items.filter(
                    (candidate) => candidate.id !== item.id,
                  ),
                })
              }
              sx={{ backgroundColor: "#FFF" }}
            />
          </React.Fragment>
        );
      })}
      <Button
        size="small"
        onClick={() => onFilterModelChange({ ...filterModel, items: [] })}
      >
        Clear all
      </Button>
    </Box>
  );
}
